/* Shared state for the docs pages' personalized snippets: the gateway base
 * URL and the agent whose agent_id gets injected into copy-paste examples.
 * Mounted on the docs section route, so the chosen agent survives switching
 * between docs tabs. When mounted under an agent (the agent's Connect tab) it
 * pins that agent instead of offering a picker. */

import { createContext, useContext, useState, type ReactNode } from "react";
import type { QueryState } from "../../analytics";
import { useAgents, type Agent } from "../../agents";
import { useCurrentUser } from "../../identity";
import {
  GATEWAY_BASE_URL,
  GATEWAY_URL_IS_PLACEHOLDER,
} from "../../services/gatewayUrl";

/** Placeholder injected into snippets when no agent is selected. */
export const AGENT_ID_PLACEHOLDER = "<your-agent-id>";

/** Placeholder for the optional config-id override in snippets. */
export const CONFIG_ID_PLACEHOLDER = "<your-config-id>";

/* Auth is two credentials on every request: the AI21 workspace API key
 * (x-ai21-key — validated by the AI21 api-server in front of the gateway) and
 * the caller's own Anthropic API key (x-api-key — forwarded untouched to
 * Anthropic). Key values are never shown after creation, so docs reference
 * them symbolically — the reader replaces the placeholders after copying. */
export const AI21_KEY_PLACEHOLDER = "<your-ai21-api-key>";
export const ANTHROPIC_KEY_PLACEHOLDER = "<your-anthropic-api-key>";
/** OpenAI API-key placeholder used by LangChain OpenAI client snippets. */
export const OPENAI_KEY_PLACEHOLDER = "<your-openai-api-key>";

/** The personalized values a snippet builder interpolates — read off this
 *  context by the callers in ./integrations, and shared with the per-integration
 *  snippet modules (./langchainSnippets). */
export interface SnippetInputs {
  gatewayUrl: string;
  agentId: string;
  /** The AI21 key value — a real key in the welcome wizard, else the placeholder. */
  ai21Key: string;
  /** Signed-in user id, sent as x-ai21-user-id for per-user attribution. */
  userId: string;
}

export interface SnippetContextValue {
  /** Gateway proxy base URL for snippets (may be the placeholder). */
  gatewayUrl: string;
  isPlaceholderUrl: boolean;
  /** The raw agents query — for pickers that need loading/error states. */
  agents: QueryState<Agent[]>;
  /** The selected agent (defaults to the first), or null when none exist. */
  agent: Agent | null;
  setAgentId: (agentId: string) => void;
  /** Refetch the agents list (after registering one in-flow), then select it. */
  selectNewAgent: (agentId: string) => void;
  /** True when the agent is fixed by the route (no picker). */
  pinned: boolean;
  /** A real AI21 key value to inject into snippets (the welcome wizard, which
   *  just created it), or null to keep the symbolic placeholder (docs pages). */
  ai21Key: string | null;
  setAi21Key: (value: string | null) => void;
  /** Signed-in user id, injected as the x-ai21-user-id snippet header. */
  userId: string;
}

const SnippetContext = createContext<SnippetContextValue | null>(null);

/**
 * Provides snippet personalization state. Pass `agentId` to pin a specific
 * agent (the agent's Connect tab); omit it for the picker-driven /docs section.
 */
export function SnippetContextProvider({
  agentId,
  children,
}: {
  agentId?: string;
  children: ReactNode;
}) {
  // Bumping `version` re-keys the agents query so a freshly registered agent
  // shows up without a full reload (the useAgents mutation pattern).
  const [version, setVersion] = useState(0);
  const agents = useAgents(version);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [ai21Key, setAi21Key] = useState<string | null>(null);
  const { id: userId } = useCurrentUser();
  const pinned = agentId !== undefined;

  const list = agents.data ?? [];
  const activeId = pinned ? agentId : selectedId;
  const agent =
    list.find((a) => a.agent_id === activeId) ??
    (pinned ? null : list[0]) ??
    null;

  const selectNewAgent = (id: string) => {
    setSelectedId(id);
    setVersion((v) => v + 1);
  };

  return (
    <SnippetContext.Provider
      value={{
        gatewayUrl: GATEWAY_BASE_URL,
        isPlaceholderUrl: GATEWAY_URL_IS_PLACEHOLDER,
        agents,
        agent,
        setAgentId: setSelectedId,
        selectNewAgent,
        pinned,
        ai21Key,
        setAi21Key,
        userId,
      }}
    >
      {children}
    </SnippetContext.Provider>
  );
}

export function useSnippetContext(): SnippetContextValue {
  const ctx = useContext(SnippetContext);
  if (!ctx) {
    throw new Error(
      "useSnippetContext must be used within a SnippetContextProvider",
    );
  }

  return ctx;
}

/* The substrings a snippet should color (CodeBlock's `highlights`): everything
 * the reader needs to attend to — the placeholders they must fill in for
 * themselves (both API keys, the config id, and the agent id / gateway URL when
 * those aren't already personalized), plus the values already filled in for
 * them (a selected agent's real id, the configured gateway URL). Either way the
 * colored spans mark exactly what's specific to their setup. */
export function useSnippetHighlights(): string[] {
  const { agent, gatewayUrl, ai21Key, userId } = useSnippetContext();
  const highlights: string[] = [
    // The AI21 key: the real value if the wizard created one, else the placeholder.
    ai21Key ?? AI21_KEY_PLACEHOLDER,
    ANTHROPIC_KEY_PLACEHOLDER,
    OPENAI_KEY_PLACEHOLDER,
    CONFIG_ID_PLACEHOLDER,
  ];
  // The gateway URL: the real one if configured, else the fill-in placeholder.
  highlights.push(gatewayUrl);
  // The agent id: the selected agent's real id, else the fill-in placeholder.
  highlights.push(agent ? agent.agent_id : AGENT_ID_PLACEHOLDER);
  // The signed-in user id, injected as x-ai21-user-id — colored like the rest.
  if (userId) highlights.push(userId);

  return highlights;
}
